<template lang="pug">
div
  aiq-dialog.modal-editor(:title="getTitle",
        v-model="modalShown"
        :show-close="false"
        :close-on-click-modal="true"
        :close-on-press-escape="true"
        @closed="closeEditor"
        v-on="$attrs")

    .field-container.full-width.is-required
      label {{ $t(`${BASE_TRANSLATION_PATH}.celebration_title`) }}
      aiq-input(v-model="celebrationItem.type"
            :disabled="isEditMode"
            size="small"
            :max-length="250")

    .field-container.full-width.is-required(v-if="availableLanguages.length > 1")
      label {{ $t(`${BASE_TRANSLATION_PATH}.language`) }}
      aiq-select(v-model="celebLang" size="small" :clearable="false" :suffix-icon="Icon.CaretBottom" :clear-icon="Icon.Close" class="sel-language")
        aiq-option(v-for="item in availableLanguages",
              :key="item.code",
              :label="item.name",
              :value="item.code")

    .field-container.full-width.is-required
      label {{ $t(`${BASE_TRANSLATION_PATH}.celebration_message`) }}
      .el-input
        .custom-rich-text-container
          rich-text(@update="onUpdateRichTextEditor"
                :showImageUpload="true"
                :inChat="false")
    .field-container.full-width
      label {{ $t(`${BASE_TRANSLATION_PATH}.occurrence`) }} &nbsp;
        aiq-tooltip(effect="dark"
              :show-after="1000"
              :content="$t(`${BASE_TRANSLATION_PATH}.tooltip.occurrence`)")
          fa-icon.question-mark(:icon="['fas', 'circle-question']")
      aiq-select(v-model="celebrationItem.occurrence" size="small" :clearable="false" :suffix-icon="Icon.CaretBottom" :clear-icon="Icon.Close" class="sel-occurrence")
        aiq-option(v-for="item in CELEBRATION_OCCURRENCE_TYPES",
              :key="item.value",
              :label="item.label",
              :value="item.value")

    .field-container.full-width
      label {{ $t(`${BASE_TRANSLATION_PATH}.rank`) }} &nbsp;
        aiq-tooltip(effect="dark"
              :show-after="1000"
              :content="$t(`${BASE_TRANSLATION_PATH}.tooltip.rank`)")
          fa-icon.question-mark(:icon="['fas', 'circle-question']")
      aiq-select(v-model="celebrationItem.rank" size="small" :clearable="false" :suffix-icon="Icon.CaretBottom" :clear-icon="Icon.Close" class="sel-rank")
        aiq-option(v-for="item in getRank",
              :key="item",
              :label="item",
              :value="item")

    .field-container.full-width
      label {{ $t(`${BASE_TRANSLATION_PATH}.visual`) }}
      aiq-select(v-model="celebrationItem.visual" :filterable="true" size="small" :clearable="false" :suffix-icon="Icon.CaretBottom" :clear-icon="Icon.Close" class="sel-visual")
        aiq-option(v-for="item in celebrationVisuals",
              :key="item.value",
              :label="item.label",
              :value="item.value")

    template(v-slot:footer)
      p.required-notice * Required
      span.spinner
        font-awesome-icon(v-if="isBusy"
              size="lg"
              icon="spinner"
              spin)
      aiq-button.button-delete(v-if="isEditMode"
            type="danger"
            :plain="true"
            :disabled="isBusy"
            @click="deleteRecord") {{ $t('global.delete') }}
      aiq-button(@click="closeEditor" :disabled="isBusy") {{ $t('global.cancel') }}
      aiq-button(:disabled="!isSubmittable || isBusy"
            type="primary"
            @click="saveRecord") {{ $t('global.save') }}

</template>

<script>
import { CELEBRATION_OCCURRENCE_TYPES, CELEBRATION_VISUAL_TYPES, CELEBRATION_RANK_LIMIT } from '@/constants/settings';
import mitt from 'mitt';
import RichText from '@/components/RichText/RichText.vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

const defaultCelebrationItem = {
  type: '',
  message: '',
  occurrence: 'once',
  rank: 1,
  visual: 'confetti',
};

export default {
  name: 'customer-tab-celebration-edit',
  components: {
    RichText,
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      celebrationItem: {
        type: { required, $autoDirty: true },
        message: { required, $autoDirty: true },
        occurrence: { $autoDirty: true },
        rank: { $autoDirty: true },
        visual: { $autoDirty: true },
      },
      celebLang: {
        required: this.availableLanguages.length > 1,
        $autoDirty: true,
      },
    };
  },
  props: {
    celebration: {
      type: Object,
      default: null,
      required: false,
    },
    celebrationIndex: {
      type: Number,
      required: false,
    },
    availableLanguages: {
      type: Object,
      required: true,
    },
    celebrationLang: {
      type: String,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    celebrationVisuals: {
      type: Array,
      default: () => CELEBRATION_VISUAL_TYPES,
      required: false,
    },
  },
  computed: {
    getTitle() {
      return !this.isEditMode ? `${this.$t(`${this.BASE_TRANSLATION_PATH}.modal.title.add`)}` : `${this.$t(`${this.BASE_TRANSLATION_PATH}.modal.title.edit`)}`;
    },
    getRank() {
      return Array.from({ length: this.CELEBRATION_RANK_LIMIT }, (_, i) => i + 1);
    },
    isEditMode() {
      return !!this.celebration;
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
  },
  data() {
    return {
      BASE_TRANSLATION_PATH: 'settings_tab.admin_tab.celebration',
      CELEBRATION_OCCURRENCE_TYPES,
      CELEBRATION_RANK_LIMIT,
      modalShown: false,
      isBusy: false,
      celebrationItem: {...defaultCelebrationItem},
      celebLang: null,
      richTextEventEmitter: mitt(),
    };
  },
  methods: {
    closeEditor() {
      this.modalShown = false;
      this.$emit('close');
    },
    saveRecord() {
      this.isBusy = true;
      this.$emit('save', this.celebrationItem, this.celebrationIndex, this.celebLang);
    },
    deleteRecord() {
      this.$aiq.confirm(
        this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.title`),
        this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.message`),
        {
          cancelButtonText: this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.cancel_button`),
          confirmButtonText: this.$t(`${this.BASE_TRANSLATION_PATH}.delete_confirm.confirm_button`),
        },
      ).then(
        () => {
          this.isBusy = true;
          this.$emit('delete', this.celebrationIndex);
        },
        () => { },
      );
    },
    onUpdateRichTextEditor(msgData) {
      this.celebrationItem.message = msgData.text === '' ? '' : msgData.html;
      if (msgData.html.includes('<img')) {
        this.celebrationItem.message = msgData.html;
      }
    },
    initCelebrationInfo(newVal = null) {
      this.celebrationItem = newVal ? {...newVal} : {...defaultCelebrationItem};
    },
  },
  watch: {
    celebrationLang: {
      handler(newVal) {
        this.celebLang = newVal;
      },
      deep: true,
      immediate: true,
    },
    isVisible: {
      handler(newVal) {
        this.modalShown = newVal;
        this.isBusy = false;

        if (this.modalShown) {
          this.initCelebrationInfo(this.celebration);
          this.$nextTick(() => this.richTextEventEmitter.emit('setText', this.celebrationItem.message || ''));
        }

      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-editor {
  &.el-dialog {
    overflow: visible !important;
  }
  footer {
    .spinner {
      margin-right: 10px;
    }
  }
}
</style>
